/**
 * useApiQuery / useApiMutation — small state machines around `portalApi`.
 *
 * Copied from `downwind-console/src/lib/api/hooks.ts`, deliberately unchanged
 * except for the error class it wraps. The two SPAs are the same shape — a
 * Worker origin, no cross-origin API, a handful of screens each doing one fresh
 * read — so a second data layer here would be two things to fix every time one
 * of them is wrong.
 *
 * Intentionally light: no cache, no stale-while-revalidate, no global store.
 * Every screen is a fresh read. The return shapes mirror React Query's, so
 * swapping it in later would not touch call sites.
 *
 * The one place the portal is stricter than the console is the toast: an
 * operator can read `503 · service_unavailable` and act on it, a customer
 * cannot. `surfaceError` therefore shows the Worker's own sentence — which is
 * written for a customer — and drops the code into the secondary line only when
 * there is one worth showing.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "../toast";
import { PortalError } from "./client";

export type ApiError = PortalError | Error;

export type UseApiQueryOptions<T> = {
  /** When false the query is dormant: no fetch, `data` stays undefined. */
  enabled?: boolean;
  /** Suppress the automatic error toast when the route renders its own error UI. */
  suppressToast?: boolean;
  onSuccess?: (data: T) => void;
};

export type UseApiQueryResult<T> = {
  data: T | undefined;
  error: ApiError | null;
  isLoading: boolean;
  /** Re-run the fetch. Resets error/loading. */
  refetch: () => void;
};

/**
 * Fire a one-off read. Re-runs when any element of `key` changes by `Object.is`
 * — pass the search terms, the route param, whatever the read depends on. Like
 * `useEffect`, stable identity matters.
 */
export function useApiQuery<T>(
  key: ReadonlyArray<unknown>,
  fn: (signal: AbortSignal) => Promise<T>,
  opts: UseApiQueryOptions<T> = {},
): UseApiQueryResult<T> {
  const { pushToast } = useToast();
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<ApiError | null>(null);
  // Seeded from `enabled`, NOT `false`. The fetch starts in a passive effect,
  // which React may flush after the browser has painted — so a hook that began
  // life "not loading, no data, no error" hands its route one committed frame
  // that is indistinguishable from a finished, empty read. On the license
  // detail screen that frame is the warn-toned "No license <id>" panel, so
  // opening a key mid-ticket could flash what looks like a 404 for a license
  // that loads fine a moment later.
  const [isLoading, setIsLoading] = useState(opts.enabled !== false);
  const [nonce, setNonce] = useState(0);

  // Stash fn/opts in refs so the effect's dep array is just `key`, and identity
  // churn in an inline arrow doesn't cause a refetch loop.
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const enabled = opts.enabled !== false;

  // biome-ignore lint/correctness/useExhaustiveDependencies: key is the dep array
  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    const ctrl = new AbortController();
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    fnRef
      .current(ctrl.signal)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setIsLoading(false);
        optsRef.current.onSuccess?.(result);
      })
      .catch((e: unknown) => {
        if (cancelled || ctrl.signal.aborted) return;
        const wrapped = wrapError(e);
        setError(wrapped);
        setIsLoading(false);
        if (!optsRef.current.suppressToast) surfaceError(wrapped, pushToast);
      });
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [enabled, nonce, pushToast, ...key]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return useMemo(() => ({ data, error, isLoading, refetch }), [data, error, isLoading, refetch]);
}

export type UseApiMutationResult<TArgs, TResult> = {
  mutate: (args: TArgs) => void;
  mutateAsync: (args: TArgs) => Promise<TResult>;
  data: TResult | undefined;
  error: ApiError | null;
  isPending: boolean;
  reset: () => void;
};

export type UseApiMutationOptions<TArgs, TResult> = {
  onSuccess?: (data: TResult, args: TArgs) => void;
  onError?: (error: ApiError, args: TArgs) => void;
  suppressToast?: boolean;
};

/**
 * Fire a write. `mutateAsync` for awaitable flows ("revoke, then navigate"),
 * `mutate` for click handlers that only want the state update.
 */
export function useApiMutation<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  opts: UseApiMutationOptions<TArgs, TResult> = {},
): UseApiMutationResult<TArgs, TResult> {
  const { pushToast } = useToast();
  const [data, setData] = useState<TResult | undefined>(undefined);
  const [error, setError] = useState<ApiError | null>(null);
  const [isPending, setIsPending] = useState(false);

  const fnRef = useRef(fn);
  fnRef.current = fn;
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const mutateAsync = useCallback(
    async (args: TArgs): Promise<TResult> => {
      setIsPending(true);
      setError(null);
      try {
        const result = await fnRef.current(args);
        setData(result);
        setIsPending(false);
        optsRef.current.onSuccess?.(result, args);
        return result;
      } catch (e: unknown) {
        const wrapped = wrapError(e);
        setError(wrapped);
        setIsPending(false);
        optsRef.current.onError?.(wrapped, args);
        if (!optsRef.current.suppressToast) surfaceError(wrapped, pushToast);
        throw wrapped;
      }
    },
    [pushToast],
  );

  const mutate = useCallback(
    (args: TArgs) => {
      // Swallow the rejection — call sites that care should use mutateAsync.
      mutateAsync(args).catch(() => {});
    },
    [mutateAsync],
  );

  const reset = useCallback(() => {
    setData(undefined);
    setError(null);
    setIsPending(false);
  }, []);

  return useMemo(
    () => ({ mutate, mutateAsync, data, error, isPending, reset }),
    [mutate, mutateAsync, data, error, isPending, reset],
  );
}

function wrapError(e: unknown): ApiError {
  if (e instanceof Error) return e;
  return new Error(String(e));
}

/**
 * Codes whose sentence is the whole story. A customer reading "Sign in to see
 * this" learns nothing from `401 · not_signed_in` underneath it, and a support
 * reference on a routine message trains people to ignore the one that matters.
 */
const CODES_WITHOUT_DETAIL = new Set([
  "not_signed_in",
  "rate_limited",
  "bad_email",
  "bad_key",
  "not_found",
  "link_invalid",
  "link_expired",
  "link_used",
  "account_blocked",
]);

function surfaceError(err: ApiError, pushToast: ReturnType<typeof useToast>["pushToast"]): void {
  // AbortError fires whenever a route unmounts mid-fetch — never surfaced.
  if (err.name === "AbortError") return;
  if (err instanceof PortalError) {
    const toast: { kind: "error"; title: string; body?: string } = {
      kind: "error",
      title: err.message,
    };
    // Kept for the failures a customer might quote at us. `service_unavailable`
    // and `internal` are the two where "what did it say exactly" is the first
    // question support asks.
    if (!CODES_WITHOUT_DETAIL.has(err.code)) toast.body = `${err.status} · ${err.code}`;
    pushToast(toast);
    return;
  }
  pushToast({
    kind: "error",
    title: err.message || "Something went wrong. Please try again.",
  });
}
